import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import './Audits.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const SUPABASE_URL = 'https://yulypcgdgxzqtdkysywg.supabase.co';
const SUPABASE_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inl1bHlwY2dkZ3h6cXRka3lzeXdnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzcwMzM4NDcsImV4cCI6MjA5MjYwOTg0N30.jeMCN3jQaIzdZor8I_ZdKLg5pBx6iiPdGQdaV26RrUw';

const AUDIT_TYPES = ['UVDB B1', 'UVDB B2', 'ISO 9001', 'ISO 14001', 'ISO 45001', 'Internal Audit', 'Other'];

const STATUS_CONFIG = {
  scheduled:   { label: 'Scheduled',   cls: 'aud-status-scheduled'  },
  'in progress': { label: 'In Progress', cls: 'aud-status-inprogress' },
  completed:   { label: 'Completed',   cls: 'aud-status-completed'  },
  'follow-up': { label: 'Follow-up',   cls: 'aud-status-followup'   },
};

const SEV_CONFIG = {
  critical: { colour: '#ef5350', bg: 'rgba(239,83,80,0.12)',  border: 'rgba(239,83,80,0.3)'  },
  major:    { colour: '#FF9800', bg: 'rgba(255,152,0,0.12)',  border: 'rgba(255,152,0,0.3)'  },
  minor:    { colour: '#f5b50b', bg: 'rgba(245,181,11,0.12)', border: 'rgba(245,181,11,0.3)' },
};

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function ScorePill({ label, mse, site }) {
  if (mse == null && site == null) return null;
  const val = mse ?? site;
  const colour = val >= 95 ? '#66bb6a' : val >= 80 ? '#f5b50b' : '#ef5350';
  return (
    <div className="score-pill">
      <span className="score-label">{label}</span>
      {mse != null && <span className="score-val" style={{ color: colour }}>{mse}%</span>}
      {site != null && <span className="score-val site" style={{ color: colour }}>{site}%</span>}
    </div>
  );
}

export default function Audits({ user }) {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'add' | 'detail'
  const [selected, setSelected] = useState(null);

  // Upload / analysis state
  const [file, setFile] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [analysed, setAnalysed] = useState(null); // raw Claude response
  const fileInputRef = useRef();

  // Form state (populated from analysis or manually)
  const [form, setForm] = useState({
    title: '', type: 'UVDB B1', auditor: '', auditing_body: '',
    completed_date: '', status: 'completed', scope: '', notes: '',
    result: '', follow_up_date: '', certificate_expiry: '', reminder_days: 90,
  });
  const [scores, setScores] = useState(null);
  const [ncrs, setNcrs] = useState([]);
  const [observations, setObservations] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAudits(); }, []);

  const fetchAudits = async () => {
    try {
      const res = await axios.get(`${API}/audits`);
      setAudits(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const toBase64 = (f) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result.split(',')[1]);
    r.onerror = rej;
    r.readAsDataURL(f);
  });

  const analyseAuditDoc = async (selectedFile) => {
    setAnalysing(true);
    setAnalysisStatus('Reading audit document...');
    try {
      const base64 = await toBase64(selectedFile);
      setAnalysisStatus('Sending to Claude for analysis...');
      const response = await axios.post(`${API}/analyse/audit`, {
        base64,
        mediaType: selectedFile.type,
      });
      const data = response.data;
      setAnalysed(data);

      // Populate form
      // Helper to convert DD/MM/YYYY to YYYY-MM-DD
      const parseAuditDate = (dateStr) => {
        if (!dateStr) return '';
        // Already in YYYY-MM-DD format
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        // Convert DD/MM/YYYY
        const parts = dateStr.split('/');
        if (parts.length === 3) return `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
        return dateStr;
      };

      setForm(prev => ({
        ...prev,
        title: data.auditing_body && data.audit_type
          ? `${data.auditing_body} ${data.audit_type} Audit`
          : prev.title,
        type: data.audit_type || prev.type,
        auditor: data.auditor_name || prev.auditor,
        auditing_body: data.auditing_body || prev.auditing_body,
        completed_date: parseAuditDate(data.audit_date) || prev.completed_date,
        status: 'completed',
      }));

      if (data.scores) setScores(data.scores);
      if (data.non_conformances) setNcrs(data.non_conformances);
      if (data.observations) setObservations(data.observations);

      setAnalysisStatus('Done! Review the details below and save.');
      setTimeout(() => setAnalysisStatus(''), 4000);
    } catch (err) {
      console.error(err);
      setAnalysisStatus('Could not analyse — please fill in the fields manually.');
      setTimeout(() => setAnalysisStatus(''), 4000);
    } finally {
      setAnalysing(false);
    }
  };

  const handleFileSelect = (f) => {
    if (!f) return;
    setFile(f);
    setAnalysed(null);
    setScores(null);
    setNcrs([]);
    setObservations([]);
    analyseAuditDoc(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileSelect(f);
  };

  const handleSave = async () => {
    if (!form.title) { alert('Title is required.'); return; }
    setSaving(true);
    let auditId = null;
    try {
      // 1. Save database record FIRST (no file yet)
      const auditRes = await axios.post(`${API}/audits`, {
        ...form,
        report_url: null,
        result: scores ? JSON.stringify(scores) : form.result || null,
        notes: observations.length > 0
          ? observations.map(o => `${o.question_number}: ${o.description}`).join('\n\n')
          : form.notes || null,
        reminder_days: parseInt(form.reminder_days) || 90,
        certificate_expiry: form.certificate_expiry || null,
        follow_up_date: form.follow_up_date || null,
        completed_date: form.completed_date || null,
        created_by: user.id,
      });

      auditId = auditRes.data.id;

      // 2. Raise NCRs — if this fails, delete the audit and abort
      if (ncrs.length > 0) {
        for (const ncr of ncrs) {
          await axios.post(`${API}/non-conformances`, {
            title: `[${ncr.question_number}] ${ncr.description.substring(0, 80)}${ncr.description.length > 80 ? '...' : ''}`,
            description: ncr.description,
            source: 'External Audit',
            severity: ncr.severity || 'minor',
            audit_id: auditId,
            status: 'open',
            notes: ncr.remote_closeout ? 'Suitable for remote close-out.' : 'Requires physical revisit for close-out.',
            raised_by: user.id,
            created_by: user.id,
          });
        }
      }

      // 3. Upload file LAST — only if DB saved successfully
      if (file) {
        const ext = file.name.split('.').pop();
        const safeName = form.title.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40);
        const path = `documents/audit/${safeName}_${auditId}.${ext}`;
        const uploadRes = await fetch(`${SUPABASE_URL}/storage/v1/object/vera-documents/${path}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${SUPABASE_KEY}`,
            'Content-Type': file.type,
            'x-upsert': 'true',
          },
          body: file,
        });
        if (uploadRes.ok) {
          const report_url = `${SUPABASE_URL}/storage/v1/object/public/vera-documents/${path}`;
          // Update the audit record with the file URL
          await axios.patch(`${API}/audits/${auditId}`, { report_url });
        }
      }

      // 4. Reset and navigate
      setView('list');
      setFile(null);
      setForm({ title: '', type: 'UVDB B1', auditor: '', auditing_body: '', completed_date: '', status: 'completed', scope: '', notes: '', result: '', follow_up_date: '', certificate_expiry: '', reminder_days: 90 });
      setScores(null);
      setNcrs([]);
      setObservations([]);
      setAnalysed(null);
      fetchAudits();
    } catch (err) {
      // Rollback — delete the audit record if it was created but something failed after
      if (auditId) {
        try {
          await axios.delete(`${API}/audits/${auditId}`);
        } catch (rollbackErr) {
          console.error('Rollback failed:', rollbackErr.message);
        }
      }
      alert('Error saving audit: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDetail = async (audit) => {
    setSelected(audit);
    setView('detail');
  };

  const counts = useMemo(() => ({
    total: audits.length,
    completed: audits.filter(a => a.status === 'completed').length,
    scheduled: audits.filter(a => a.status === 'scheduled').length,
    followup: audits.filter(a => a.status === 'follow-up').length,
  }), [audits]);

  if (loading) return <div className="aud-loading">Loading audits...</div>;

  // ── ADD AUDIT ────────────────────────────────────────────────
  if (view === 'add') return (
    <div className="aud-add-page">
      <button className="aud-back" onClick={() => { setView('list'); setFile(null); setAnalysed(null); setScores(null); setNcrs([]); setObservations([]); }}>← AUDITS</button>
      <div className="aud-add-header">
        <h2>ADD AUDIT RECORD</h2>
        <p>Upload an audit declaration or report and Claude will extract all the details automatically</p>
      </div>

      {/* Drop zone */}
      <div
        className={`aud-drop-zone ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
        onDrop={handleDrop}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => fileInputRef.current?.click()}
      >
        {analysing ? (
          <div className="aud-drop-analysing"><div className="aud-spinner" /><span>{analysisStatus}</span></div>
        ) : file ? (
          <div className="aud-drop-file">
            <span className="aud-drop-icon">📄</span>
            <div>
              <div className="aud-drop-name">{file.name}</div>
              <div className="aud-drop-size">{(file.size / 1024).toFixed(0)} KB — {analysisStatus || 'Fields filled below'}</div>
            </div>
            <button className="aud-change-btn" onClick={e => { e.stopPropagation(); fileInputRef.current?.click(); }}>CHANGE</button>
          </div>
        ) : (
          <div className="aud-drop-empty">
            <div className="aud-drop-up">⬆</div>
            <div className="aud-drop-text">Drop your audit declaration here or click to browse</div>
            <div className="aud-drop-hint">PDF, Word or image — Claude will read and fill in all the details</div>
          </div>
        )}
        <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" onChange={e => handleFileSelect(e.target.files[0])} style={{ display: 'none' }} />
      </div>

      {analysisStatus && !analysing && <div className="aud-analysis-status">{analysisStatus}</div>}

      {/* Scores preview */}
      {scores && (
        <div className="aud-scores-preview">
          <div className="aud-section-title">AUDIT SCORES</div>
          <div className="aud-scores-grid">
            <div className="score-header"><span>Discipline</span><span>MSE</span><span>Site</span></div>
            {[
              { key: 'hs',           label: 'Health & Safety' },
              { key: 'environment',  label: 'Environment' },
              { key: 'quality',      label: 'Quality' },
              { key: 'sustainability', label: 'Sustainability' },
            ].map(({ key, label }) => {
              const mse = scores[`${key}_mse`];
              const site = scores[`${key}_site`];
              if (mse == null && site == null) return null;
              const colour = (v) => v >= 95 ? '#66bb6a' : v >= 80 ? '#f5b50b' : '#ef5350';
              return (
                <div key={key} className="score-row">
                  <span>{label}</span>
                  <span style={{ color: mse != null ? colour(mse) : '#444' }}>{mse != null ? `${mse}%` : '—'}</span>
                  <span style={{ color: site != null ? colour(site) : '#444' }}>{site != null ? `${site}%` : '—'}</span>
                </div>
              );
            })}
            {(scores.overall_mse != null || scores.overall_site != null) && (
              <div className="score-row overall">
                <span>Overall</span>
                <span style={{ color: scores.overall_mse >= 95 ? '#66bb6a' : '#f5b50b' }}>{scores.overall_mse != null ? `${scores.overall_mse}%` : '—'}</span>
                <span style={{ color: scores.overall_site >= 95 ? '#66bb6a' : '#f5b50b' }}>{scores.overall_site != null ? `${scores.overall_site}%` : '—'}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NCRs preview */}
      {ncrs.length > 0 && (
        <div className="aud-ncrs-preview">
          <div className="aud-section-title">NON-CONFORMANCES FOUND — {ncrs.length} will be raised automatically</div>
          {ncrs.map((ncr, i) => {
            const sev = SEV_CONFIG[ncr.severity] || SEV_CONFIG.minor;
            return (
              <div key={i} className="aud-ncr-row">
                <span className="aud-ncr-sev" style={{ color: sev.colour, background: sev.bg, border: `1px solid ${sev.border}` }}>{ncr.severity}</span>
                <span className="aud-ncr-qnum">{ncr.question_number}</span>
                <span className="aud-ncr-desc">{ncr.description}</span>
                <span className={`aud-ncr-remote ${ncr.remote_closeout ? 'yes' : 'no'}`}>{ncr.remote_closeout ? 'Remote ✓' : 'Revisit'}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Observations preview */}
      {observations.length > 0 && (
        <div className="aud-obs-preview">
          <div className="aud-section-title">OBSERVATIONS — {observations.length} noted</div>
          {observations.map((obs, i) => (
            <div key={i} className="aud-obs-row">
              <span className="aud-obs-qnum">{obs.question_number}</span>
              <span className="aud-obs-desc">{obs.description}</span>
            </div>
          ))}
        </div>
      )}

      {/* Form fields */}
      <div className="aud-form">
        <div className="aud-section-title">AUDIT DETAILS</div>
        <div className="aud-form-grid">
          <div className="aud-field full">
            <label>TITLE *</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Achilles UVDB B1 Audit 2025" />
          </div>
          <div className="aud-field">
            <label>AUDIT TYPE</label>
            <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              {AUDIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="aud-field">
            <label>AUDITING BODY</label>
            <input value={form.auditing_body} onChange={e => setForm({ ...form, auditing_body: e.target.value })} placeholder="e.g. Achilles" />
          </div>
          <div className="aud-field">
            <label>AUDITOR NAME</label>
            <input value={form.auditor} onChange={e => setForm({ ...form, auditor: e.target.value })} placeholder="Auditor's name" />
          </div>
          <div className="aud-field">
            <label>AUDIT DATE</label>
            <input type="date" value={form.completed_date} onChange={e => setForm({ ...form, completed_date: e.target.value })} />
          </div>
          <div className="aud-field">
            <label>STATUS</label>
            <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              <option value="scheduled">Scheduled</option>
              <option value="in progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="follow-up">Follow-up Required</option>
            </select>
          </div>
          <div className="aud-field">
            <label>CERTIFICATE EXPIRY</label>
            <input type="date" value={form.certificate_expiry} onChange={e => setForm({ ...form, certificate_expiry: e.target.value })} />
          </div>
          <div className="aud-field">
            <label>FOLLOW-UP DATE</label>
            <input type="date" value={form.follow_up_date} onChange={e => setForm({ ...form, follow_up_date: e.target.value })} />
          </div>
          <div className="aud-field full">
            <label>SCOPE</label>
            <input value={form.scope} onChange={e => setForm({ ...form, scope: e.target.value })} placeholder="What the audit covered" />
          </div>
          <div className="aud-field full">
            <label>ADDITIONAL NOTES</label>
            <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any additional notes..." />
          </div>
        </div>
        <div className="aud-form-actions">
          <button className="aud-btn secondary" onClick={() => { setView('list'); setFile(null); setAnalysed(null); setScores(null); setNcrs([]); setObservations([]); }}>CANCEL</button>
          <button className={`aud-btn primary ${saving ? 'disabled' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? 'SAVING...' : `SAVE AUDIT${ncrs.length > 0 ? ` + RAISE ${ncrs.length} NCR${ncrs.length > 1 ? 'S' : ''}` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );

  // ── DETAIL VIEW ──────────────────────────────────────────────
  if (view === 'detail' && selected) {
    let parsedScores = null;
    try { parsedScores = selected.result ? JSON.parse(selected.result) : null; } catch (e) {}
    const colour = (v) => v >= 95 ? '#66bb6a' : v >= 80 ? '#f5b50b' : '#ef5350';

    return (
      <div className="aud-detail-page">
        <button className="aud-back" onClick={() => { setView('list'); setSelected(null); }}>← AUDITS</button>
        <div className="aud-detail-header">
          <div className="aud-detail-ref">{selected.reference_number}</div>
          <h2 className="aud-detail-title">{selected.title}</h2>
          <div className="aud-detail-meta">
            <span className={`aud-status-badge ${STATUS_CONFIG[selected.status]?.cls || ''}`}>{STATUS_CONFIG[selected.status]?.label || selected.status}</span>
            {selected.auditor && <span className="aud-detail-auditor">Auditor: {selected.auditor}</span>}
            {selected.completed_date && <span className="aud-detail-date">{formatDate(selected.completed_date)}</span>}
          </div>
        </div>

        <div className="aud-detail-grid">
          <div className="aud-detail-col">
            <div className="aud-detail-block">
              <div className="aud-block-title">DETAILS</div>
              <div className="aud-block-row"><span>Type</span><span>{selected.type}</span></div>
              <div className="aud-block-row"><span>Auditing Body</span><span>{selected.auditing_body || '—'}</span></div>
              <div className="aud-block-row"><span>Auditor</span><span>{selected.auditor || '—'}</span></div>
              <div className="aud-block-row"><span>Date</span><span>{formatDate(selected.completed_date)}</span></div>
              {selected.certificate_expiry && <div className="aud-block-row"><span>Cert Expiry</span><span>{formatDate(selected.certificate_expiry)}</span></div>}
              {selected.follow_up_date && <div className="aud-block-row"><span>Follow-up</span><span>{formatDate(selected.follow_up_date)}</span></div>}
            </div>

            {selected.scope && (
              <div className="aud-detail-block">
                <div className="aud-block-title">SCOPE</div>
                <p className="aud-block-text">{selected.scope}</p>
              </div>
            )}

            {selected.notes && (
              <div className="aud-detail-block">
                <div className="aud-block-title">OBSERVATIONS & NOTES</div>
                <p className="aud-block-text">{selected.notes}</p>
              </div>
            )}

            <div className="aud-detail-actions">
              {selected.report_url && (
                <a href={selected.report_url} target="_blank" rel="noreferrer" className="aud-btn primary">VIEW DECLARATION</a>
              )}
            </div>
          </div>

          {parsedScores && (
            <div className="aud-detail-col">
              <div className="aud-detail-block">
                <div className="aud-block-title">AUDIT SCORES</div>
                <div className="aud-scores-grid">
                  <div className="score-header"><span>Discipline</span><span>MSE</span><span>Site</span></div>
                  {[
                    { key: 'hs',           label: 'Health & Safety' },
                    { key: 'environment',  label: 'Environment' },
                    { key: 'quality',      label: 'Quality' },
                    { key: 'sustainability', label: 'Sustainability' },
                  ].map(({ key, label }) => {
                    const mse = parsedScores[`${key}_mse`];
                    const site = parsedScores[`${key}_site`];
                    if (mse == null && site == null) return null;
                    return (
                      <div key={key} className="score-row">
                        <span>{label}</span>
                        <span style={{ color: mse != null ? colour(mse) : '#444' }}>{mse != null ? `${mse}%` : '—'}</span>
                        <span style={{ color: site != null ? colour(site) : '#444' }}>{site != null ? `${site}%` : '—'}</span>
                      </div>
                    );
                  })}
                  {(parsedScores.overall_mse != null || parsedScores.overall_site != null) && (
                    <div className="score-row overall">
                      <span>Overall</span>
                      <span style={{ color: colour(parsedScores.overall_mse) }}>{parsedScores.overall_mse != null ? `${parsedScores.overall_mse}%` : '—'}</span>
                      <span style={{ color: colour(parsedScores.overall_site) }}>{parsedScores.overall_site != null ? `${parsedScores.overall_site}%` : '—'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────
  return (
    <div className="aud-page">
      {/* Stat cards */}
      <div className="aud-stats">
        <div className="aud-stat"><span className="aud-stat-value">{counts.total}</span><span className="aud-stat-label">Total</span></div>
        <div className="aud-stat completed"><span className="aud-stat-value">{counts.completed}</span><span className="aud-stat-label">Completed</span></div>
        <div className="aud-stat scheduled"><span className="aud-stat-value">{counts.scheduled}</span><span className="aud-stat-label">Scheduled</span></div>
        <div className="aud-stat followup"><span className="aud-stat-value">{counts.followup}</span><span className="aud-stat-label">Follow-up</span></div>
      </div>

      <div className="aud-controls">
        {user.role === 'admin' && (
          <button className="aud-add-btn" onClick={() => setView('add')}>+ ADD AUDIT</button>
        )}
      </div>

      {audits.length === 0 ? (
        <div className="aud-empty">
          <div className="aud-empty-icon">📋</div>
          <p>No audit records yet.</p>
          {user.role === 'admin' && <button className="aud-btn primary" onClick={() => setView('add')}>Add your first audit</button>}
        </div>
      ) : (
        <div className="aud-list">
          {audits.map(audit => {
            let parsedScores = null;
            try { parsedScores = audit.result ? JSON.parse(audit.result) : null; } catch (e) {}
            const colour = (v) => v >= 95 ? '#66bb6a' : v >= 80 ? '#f5b50b' : '#ef5350';

            return (
              <div key={audit.id} className="aud-card" onClick={() => openDetail(audit)}>
                <div className="aud-card-top">
                  <div>
                    <div className="aud-card-ref">{audit.reference_number}</div>
                    <div className="aud-card-title">{audit.title}</div>
                    <div className="aud-card-meta">
                      {audit.auditing_body && <span>{audit.auditing_body}</span>}
                      {audit.auditor && <span>· {audit.auditor}</span>}
                      {audit.completed_date && <span>· {formatDate(audit.completed_date)}</span>}
                    </div>
                  </div>
                  <div className="aud-card-right">
                    <span className={`aud-status-badge ${STATUS_CONFIG[audit.status]?.cls || ''}`}>
                      {STATUS_CONFIG[audit.status]?.label || audit.status}
                    </span>
                    {parsedScores?.overall_mse != null && (
                      <span className="aud-overall-score" style={{ color: colour(parsedScores.overall_mse) }}>
                        {parsedScores.overall_mse}%
                      </span>
                    )}
                  </div>
                </div>
                {parsedScores && (
                  <div className="aud-card-scores">
                    {[
                      { key: 'hs', label: 'H&S' },
                      { key: 'environment', label: 'Env' },
                      { key: 'quality', label: 'Quality' },
                      { key: 'sustainability', label: 'Sustainability' },
                    ].map(({ key, label }) => {
                      const mse = parsedScores[`${key}_mse`];
                      const site = parsedScores[`${key}_site`];
                      if (mse == null && site == null) return null;
                      return (
                        <div key={key} className="aud-score-chip">
                          <span className="aud-score-chip-label">{label}</span>
                          {mse != null && <span style={{ color: colour(mse) }}>{mse}%</span>}
                          {site != null && <span className="site-score" style={{ color: colour(site) }}> / {site}%</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
